import './MyPagePanel.scss';
import * as React from 'react';
import classNames from 'classnames';
import Switch from '../FormElements/Switch';
import logo from '../../images/Logos/3f-logo.svg';
import eLearningLogo from '../../images/e_learning.png';
import streamingLogo from '../../images/StreamingLink.png';
import schultzLogo from '../../images/SchultzLogo.png';
import { formatDate } from '../../utils/dateUtils';
import { Link } from 'react-router-dom';
import { IActivityModel } from 'src/Entities/Activity/Activity';
import { IComingCourses } from 'src/Entities/User/User';

type MyPagePanelProps = {
  isExpanded: boolean;
  onlyShowRelevant: boolean;
  favorites: Array<Pick<IActivityModel, 'courseTitle' | 'detailKursusId'>>;
  upcoming: IComingCourses[];
  onExpandClick: () => void;
  onOnlyShowRelevantChange: (value: boolean) => void;
}

const MyPagePanel = ({
  isExpanded,
  onlyShowRelevant,
  favorites,
  upcoming,
  onExpandClick,
  onOnlyShowRelevantChange,
}: MyPagePanelProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleOutSideClick = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        event.preventDefault();
        onExpandClick();
      }
    };

    if (isExpanded && ref.current) {
      document.addEventListener('click', handleOutSideClick);
      document.addEventListener('touchstart', handleOutSideClick);
    }

    return () => {
      if (isExpanded) {
        document.removeEventListener('click', handleOutSideClick);
        document.removeEventListener('touchstart', handleOutSideClick);
      }
    };
  }, [isExpanded]);

  return (
    <div
      ref={ref}
      className={classNames('MyPagePanel', isExpanded && 'is-expanded')}
    >
      <button
        className="MyPagePanelToggle"
        onClick={onExpandClick}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          fill="none"
          viewBox="0 0 18 18"
        >
          <path
            stroke="#000"
            d="M5.748 11.5L3.246 9l2.502-2.502M3.246 8.999h11.508"
          />
        </svg>
        Min side
      </button>
      <div className="MyPagePanelContent f5">
        <div className="border-b border-gray-500 pb-4">
          <div className="flex justify-between items-center">
            <div className="font-bold uppercase">Vis kun relevante kurser</div>
            <Switch
              size="small"
              checked={onlyShowRelevant}
              onChange={() => onOnlyShowRelevantChange(!onlyShowRelevant)}
            />
          </div>
          <Link to="/min-side" className="block underline hover:no-underline mt-1">Rediger liste</Link>
        </div>

        <div className="border-b border-gray-500 py-4">
          <div className="font-bold uppercase">{`Mine kursusfavoritter (${favorites.length})`}</div>
          <Link to="/min-side" className="block underline hover:no-underline mt-1">Se alle og rediger favoritter</Link>

          {favorites.length === 0 && (
            <div className="has-text-gray-700 mt-4">Du har ingen favoritter</div>
          )}
          {favorites.length > 0 && (
            <ul className="ml-0 pl-4 font-semibold">
              {favorites.slice(0, 3).map((favorite) => (
                <li key={favorite.detailKursusId}>
                  <Link to={`/kursus/${favorite.detailKursusId}`} className="hover:underline">{favorite.courseTitle}</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-b border-gray-500 py-4">
          <div className="font-bold uppercase">{`Mine kommende kurser (${upcoming.length})`}</div>
          <Link to="/min-side" className="block underline hover:no-underline mt-1">Se alle tilmeldte kurser</Link>

          {upcoming.length === 0 && (
            <div className="has-text-gray-700 mt-4">Du har ingen kommende kurser</div>
          )}
          {upcoming.length > 0 && (
            <ul className="ml-0 pl-0 font-semibold list-none space-y-5">
              {upcoming.slice(0, 3).map((course) => (
                <li key={course.detailKursusId}>
                  <Link to={`/kursus/${course.detailKursusId}`} className="flex">
                    <img src={logo} className="has-background-gray-body w-16 h-16 mr-2" />
                    <div>
                      <div className="font-light">
                        {formatDate(new Date(course.activity.startDate), 'dd. MMMM')} - {course.activity.startTime}
                      </div>
                      <span className="underline hover:no-underline">{course.activity.courseTitle}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-b border-gray-500 py-4">
          <div className="font-bold uppercase mb-4">3F E-læringsportal</div>
          <div className="flex font-semibold">
            <img src={eLearningLogo} className="has-background-gray-body w-16 h-16 mr-2" />
            <a
              href="https://3f.myacademy.dk"
              target="_blank"
              className="underline hover:no-underline"
            >
              Gå på opdagelse i 3F's store E-læringsportal
            </a>
          </div>
        </div>

        <div className="border-b border-gray-500 py-4">
          <div className="font-bold uppercase mb-4">3F Video fra URO</div>
          <div className="flex font-semibold">
            <img src={streamingLogo} className="has-background-gray-body w-16 h-16 mr-2" />
            <a
              href="https://web.microsoftstream.com/group/813f45bf-17f6-479c-9d16-f5f552c896f2"
              target="_blank"
              className="underline hover:no-underline"
            >
              Gå på opdagelse i 3F's store E-læringsportal
            </a>
          </div>
        </div>

        <div className="py-4">
          <div className="font-bold uppercase mb-4">Schultz E-læring</div>
          <div className="flex font-semibold">
            <img src={schultzLogo} className="has-background-gray-body w-16 h-16 mr-2" />
            <a
              href="https://web.microsoftstream.com/group/813f45bf-17f6-479c-9d16-f5f552c896f2"
              target="_blank"
              className="underline hover:no-underline"
            >
              Find nye muligheder og veje for udvikling i Schultz's E-læringskatalog
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyPagePanel;



// WEBPACK FOOTER //
// ./src/components/MyPagePanel/index.tsx